import Big from "big.js";
import { CostType, Stage } from "../../shared/CaseConfiguration";
import { percentage } from "../../shared/percentage";
import {
  EntityRef,
  LedgerItems,
  OriginItem,
  PaymentItem,
  entityOf,
  isOriginOfKind,
  opponentIdOf,
} from "../../shared/ledger/items";
import { signedAmountForItem } from "../../shared/ledger/project";
import { vatOn } from "../../shared/vat";
import {
  badge,
  card,
  font,
  GraphColors,
  graphStart,
  quoted,
  sectionLabel,
  signedTone,
  td,
  text,
  tr,
} from "./graphDot";

/** What the drawings of a ledger take from the app. */
export interface LedgerLook {
  colors: GraphColors;
  money: (value: number) => string;
  stageName: (stage: Stage) => string;
  costTypeName: (type: CostType) => string;
}

const pct = (fraction: number): string => percentage.format(fraction);

/** What a payment is, told from its mechanism and from what it cites. */
function describe(
  payment: PaymentItem,
  items: LedgerItems,
): { kind: string; detail?: string } {
  const derivation = payment.causedBy.derivation;
  const cited = payment.causedBy.origins.flatMap((id) => {
    const origin = items.origins.find((o) => o.id === id);
    return origin === undefined ? [] : [origin];
  });
  switch (payment.mechanism) {
    case "fee":
      if (derivation?.rule === "successFee")
        return {
          kind: "Success fee",
          detail: `${pct(derivation.percentage)} of the ${pct(1 - derivation.discount)} at risk`,
        };
      if (derivation?.rule === "lowFee")
        return {
          kind: "No-win fee",
          detail: `${pct(derivation.discount)} of the base`,
        };
      return { kind: "Base fee" };
    case "billing":
      if ((payment.causedBy.payments?.length ?? 0) > 0)
        return { kind: "Re-charged fees" };
      return {
        kind:
          derivation?.rule === "enforcementApportionment"
            ? "Enforcement cost"
            : "Bill",
      };
    case "absorption":
      return {
        kind: "Own cost of the work",
        detail:
          derivation?.rule === "margin"
            ? `margin ${pct(derivation.percentage)}`
            : undefined,
      };
    case "adjustment": {
      // told apart by the facts they cite
      const award = cited.find(isOriginOfKind("quantumAward"));
      if (award)
        return {
          kind:
            award.direction === "awarded"
              ? "Judgment debt"
              : "Judgment debt to pay",
          detail: award.quantumName,
        };
      const settlement = cited.find(isOriginOfKind("settlement"));
      if (settlement)
        return {
          kind: "Settlement payment",
          detail: `with ${settlement.counterpartyName}`,
        };
      if (derivation?.rule === "recovery") return { kind: "Costs recovered" };
      if (derivation?.rule === "adverseCost")
        return { kind: "Costs of the other side" };
      return { kind: "Enforcement shortfall" };
    }
  }
}

/**
 * Dev tool: the ledger of one party for one outcome, read as a statement.
 * Plain dev strings throughout, no i18n; the names of stages and of the types
 * of cost are the app's.
 *
 * The ledger is a graph of facts (origins) and of the payments they cause.
 * Most of it is the same four items over and over: a piece of work, its bill,
 * the provider's own cost of it and what a costs order returns of it. Those
 * are the rows of one table here, and what the party pays of the other
 * side's costs the rows of another. The facts and the payments that are no
 * such row (awards, fees, settlements) are cards, joined by what causes what,
 * and all of it ends in the party's net result: the sum of its payments,
 * which is how the ledger defines it.
 */
export function ledgerItemsToDot(
  items: LedgerItems,
  partyName: string,
  look: LedgerLook,
): string {
  const { colors, money, stageName, costTypeName } = look;
  const signed = (value: number) => (value > 0 ? "+" : "") + money(value);
  const quiet = (content: string, size = 9) =>
    font(content, { size, color: colors.quiet });
  const forParty = (payment: PaymentItem) =>
    signedAmountForItem(payment, "party");
  const sum = (payments: PaymentItem[], amount = forParty) =>
    payments.reduce((total, p) => total.add(amount(p)), new Big(0)).toNumber();
  const who = (ref: EntityRef): string => {
    const opponentId = opponentIdOf(ref);
    if (opponentId !== undefined)
      return items.context.partyNames[opponentId] ?? opponentId;
    switch (ref) {
      case "party":
        return partyName;
      case "lawFirm":
        return "law firm";
      case "barrister":
        return "barrister";
      default:
        return "outside";
    }
  };
  const columnHead = (label: string, align: "LEFT" | "RIGHT" = "LEFT") =>
    td(
      font(label.toUpperCase(), { size: 7, color: colors.quiet, bold: true }),
      {
        align,
        bg: colors.soft,
      },
    );
  const figure = (value: number, size = 9.5) =>
    value === 0
      ? font("–", { size, color: colors.quiet })
      : font(signed(value), {
          size,
          color: signedTone(colors, value).ink,
          bold: true,
        });

  // ---- what there is to draw
  // The budget emits a piece of work per stage and type even at amount 0. One
  // that no payment cites says nothing; one with an amount stays, cited or not.
  const citedOrigins = new Set(
    items.payments.flatMap((p) => p.causedBy.origins),
  );
  const origins = items.origins.filter(
    (o) => citedOrigins.has(o.id) || o.kind !== "work" || o.amount !== 0,
  );
  const work = origins
    .filter(isOriginOfKind("work"))
    .sort((a, b) => a.stage - b.stage || a.costType.localeCompare(b.costType));
  const rowOf = new Map(work.map((w, row) => [w.id, row]));
  const workCitedBy = (p: PaymentItem) =>
    p.causedBy.origins.filter((id) => rowOf.has(id));

  type Line = {
    bills: PaymentItem[];
    own: PaymentItem[];
    recoveries: PaymentItem[];
  };
  const lines: Line[] = work.map(() => ({
    bills: [],
    own: [],
    recoveries: [],
  }));
  const adverse: PaymentItem[] = [];
  const others: PaymentItem[] = [];
  // the row of the costs table a payment is part of, or the node it is
  const placeOf = new Map<string, string>();
  for (const payment of items.payments) {
    const cites = workCitedBy(payment);
    const rule = payment.causedBy.derivation?.rule;
    const line =
      cites.length === 1 ? lines[rowOf.get(cites[0]) ?? 0] : undefined;
    const part =
      line === undefined
        ? undefined
        : rule === "recovery"
          ? line.recoveries
          : payment.mechanism === "absorption"
            ? line.own
            : payment.mechanism === "billing" &&
                (payment.causedBy.payments?.length ?? 0) === 0
              ? line.bills
              : undefined;
    if (part !== undefined) {
      part.push(payment);
      placeOf.set(payment.id, `costs:e${rowOf.get(cites[0])}:e`);
    } else if (rule === "adverseCost") {
      adverse.push(payment);
      placeOf.set(payment.id, "adverse");
    } else {
      others.push(payment);
      placeOf.set(payment.id, quoted(payment.id));
    }
  }

  const out = graphStart(colors);

  // ---- the party's own costs
  const billed = lines.flatMap(({ bills }) => bills);
  const recovered = lines.flatMap(({ recoveries }) => recoveries);
  if (work.length > 0) {
    const ownCost = new Map<EntityRef, PaymentItem[]>();
    for (const payment of lines.flatMap(({ own }) => own))
      ownCost.set(payment.payer, [
        ...(ownCost.get(payment.payer) ?? []),
        payment,
      ]);
    const rows = work.map((w, row) => {
      const { bills, own, recoveries } = lines[row];
      const route = [
        ...new Set(
          bills.map(({ payer, payee }) =>
            payer === "party"
              ? `to ${who(payee)}`
              : `${who(payer)} to ${who(payee)}`,
          ),
        ),
      ];
      // The caps of the recoveries — of the work and of the VAT on it — are
      // their rate of the work as the party paid it, with that VAT.
      const paid = new Big(w.amount)
        .add(vatOn(w.amount, w.costType, items.context.vatRate))
        .toNumber();
      const cap = recoveries.reduce(
        (sum, { causedBy: { derivation } }) =>
          derivation?.rule === "recovery"
            ? sum.add(derivation.maxRecoverable)
            : sum,
        new Big(0),
      );
      const rate =
        recoveries.length > 0 && paid !== 0
          ? pct(cap.div(paid).toNumber())
          : undefined;
      return tr([
        td(" ", {
          bg: w.estimated ? colors.estimated : colors.incurred,
          port: `w${row}`,
        }),
        td(quiet(text(stageName(w.stage)), 9.5)),
        td(font(text(costTypeName(w.costType)), { size: 9.5 })),
        td(font(money(w.amount), { size: 9.5 }), { align: "RIGHT" }),
        td(
          quiet(
            route.length > 0
              ? text(route.join(", "))
              : own.length > 0
                ? `borne by the ${text(who(own[0].payer))}`
                : "not billed",
          ),
        ),
        td(figure(sum(bills)), { align: "RIGHT" }),
        td(
          recoveries.length > 0
            ? `${figure(sum(recoveries))}${rate ? ` ${quiet(rate, 8)}` : ""}`
            : font("–", { size: 9.5, color: colors.quiet }),
          { align: "RIGHT", port: `e${row}` },
        ),
      ]);
    });
    out.push(
      `  costs [label=<${card(colors, [
        tr([
          td(
            sectionLabel(
              colors,
              `Costs of ${partyName}, and what is recovered of them`,
            ),
            {
              colspan: 7,
              pad: 7,
              port: "title",
            },
          ),
        ]),
        tr([
          columnHead(" "),
          columnHead("Stage"),
          columnHead("Work"),
          columnHead("Amount", "RIGHT"),
          columnHead("Billed"),
          columnHead("Paid", "RIGHT"),
          columnHead("Recovered", "RIGHT"),
        ]),
        ...rows,
        tr([
          td(" ", { rule: colors.rule }),
          td(font("Total", { size: 9.5, bold: true }), {
            colspan: 4,
            rule: colors.rule,
          }),
          td(figure(sum(billed), 10.5), { align: "RIGHT", rule: colors.rule }),
          td(figure(sum(recovered), 10.5), {
            align: "RIGHT",
            rule: colors.rule,
          }),
        ]),
        tr([
          td(
            [
              `${font("■", { size: 8, color: colors.incurred })} ${quiet("incurred", 8)}`,
              `${font("■", { size: 8, color: colors.estimated })} ${quiet("estimated", 8)}`,
              ...(items.context.vatRate !== undefined
                ? [
                    quiet(
                      `paid with ${pct(items.context.vatRate)} VAT, none on court fees`,
                      8,
                    ),
                  ]
                : []),
              ...[...ownCost].map(([payer, payments]) =>
                quiet(
                  `own cost of the work of the ${text(who(payer))}: ${money(sum(payments, (p) => p.amount))}`,
                  8,
                ),
              ),
            ].join("   "),
            { colspan: 7 },
          ),
        ]),
      ])}>];`,
    );
  }

  // ---- what the party pays of the other side's costs
  if (adverse.length > 0) {
    out.push(
      `  adverse [label=<${card(colors, [
        tr([
          td(
            sectionLabel(
              colors,
              `Costs of the other side that ${partyName} pays`,
            ),
            {
              colspan: 4,
              pad: 7,
            },
          ),
        ]),
        tr([
          columnHead("To"),
          columnHead("Their work"),
          columnHead("Stage"),
          columnHead("Paid", "RIGHT"),
        ]),
        ...adverse.map((payment) => {
          const { derivation } = payment.causedBy;
          return tr([
            td(font(text(who(payment.payee)), { size: 9.5 })),
            td(
              font(
                derivation?.rule === "adverseCost"
                  ? text(costTypeName(derivation.costType))
                  : "–",
                { size: 9.5 },
              ),
            ),
            td(
              quiet(
                derivation?.rule === "adverseCost"
                  ? text(stageName(derivation.costStage))
                  : "–",
                9.5,
              ),
            ),
            td(figure(forParty(payment)), { align: "RIGHT" }),
          ]);
        }),
        tr([
          td(font("Total", { size: 9.5, bold: true }), {
            colspan: 3,
            rule: colors.rule,
          }),
          td(figure(sum(adverse), 10.5), { align: "RIGHT", rule: colors.rule }),
        ]),
      ])}>];`,
    );
  }

  // ---- the facts that cause payments
  const fact = (
    origin: OriginItem,
    kind: string,
    title: string,
    details: string[],
    outline?: string,
  ) =>
    out.push(
      `  ${quoted(origin.id)} [label=<${card(
        colors,
        [
          tr([td(sectionLabel(colors, kind), { pad: 7 })]),
          tr([td(font(text(title), { size: 10.5, bold: true }))]),
          ...details.map((detail) => tr([td(quiet(text(detail)))])),
        ],
        { outline },
      )}>];`,
    );
  const names = (shares: ReadonlyArray<{ partyId: string; share: number }>) =>
    shares
      .map(
        ({ partyId, share }) =>
          `${items.context.partyNames[partyId] ?? partyId} (${pct(share)})`,
      )
      .join(", ");
  for (const origin of origins) {
    switch (origin.kind) {
      case "work":
        break;
      case "costsOrder": {
        // one rule per type of cost, whichever stage it was written for
        const rules = [
          ...new Map(origin.rules.map((r) => [r.costType, r.percentage])),
        ].map(([type, share]) => `${costTypeName(type)}: ${pct(share)}`);
        if (origin.direction === "awarded") {
          const derivation = recovered[0]?.causedBy.derivation;
          fact(origin, "Costs order", `Awarded to ${partyName}`, [
            ...rules,
            ...(derivation?.rule === "recovery" &&
            derivation.payerShares.length > 0
              ? [`payable by ${names(derivation.payerShares)}`]
              : []),
          ]);
          if (work.length > 0)
            out.push(
              `  ${quoted(origin.id)} -> costs:title:n [style=dashed, label=<sets what is recovered>];`,
            );
        } else {
          fact(
            origin,
            "Costs order",
            `Against ${partyName}${origin.recipientPartyId ? `, for ${items.context.partyNames[origin.recipientPartyId] ?? origin.recipientPartyId}` : ""}`,
            rules,
            colors.adverse.ink,
          );
        }
        break;
      }
      case "quantumAward":
        fact(
          origin,
          origin.direction === "awarded"
            ? "Awarded by the court"
            : "To pay by order of the court",
          `${origin.quantumName}: ${money(origin.amount)}`,
          origin.liableOpponents?.length
            ? [`liable: ${names(origin.liableOpponents)}`]
            : [],
          (origin.direction === "awarded" ? colors.favourable : colors.adverse)
            .ink,
        );
        break;
      case "enforcementPlan":
        fact(
          origin,
          "Enforcement plan (the worst case)",
          origin.value < 0
            ? `Pays ${money(-origin.value)}`
            : `Collects ${money(origin.value)}`,
          [`at a cost of ${money(origin.totalCost)}`],
        );
        break;
      case "agreement":
        fact(
          origin,
          "Fee agreement",
          origin.agreement === "lawFirmCfa"
            ? "CFA with the law firm"
            : "CFA with the barrister",
          [
            `engaged by ${origin.engagedBy === "party" ? partyName : "the law firm"}`,
            ...(origin.includedDisbursements?.length
              ? [
                  `covers ${origin.includedDisbursements.map(costTypeName).join(", ")}`,
                ]
              : []),
            ...(origin.excludedStages?.length
              ? [`not for ${origin.excludedStages.map(stageName).join(", ")}`]
              : []),
          ],
        );
        break;
      case "settlement":
        fact(origin, "Settlement", `With ${origin.counterpartyName}`, [
          `at ${stageName(origin.stage)}`,
        ]);
        break;
    }
  }

  // ---- the payments that are no row of a table
  // A judgment debt that several opponents are liable for is one payment,
  // from "external": it is any of them who pays it.
  const payerOf = (payment: PaymentItem): string => {
    const award = payment.causedBy.origins
      .map((id) => origins.find((o) => o.id === id))
      .find(isOriginOfKind("quantumAward"));
    return payment.payer === "external" && award?.liableOpponents?.length
      ? award.liableOpponents
          .map(({ partyId }) => items.context.partyNames[partyId] ?? partyId)
          .join(" or ")
      : who(payment.payer);
  };
  for (const payment of others) {
    const amount = forParty(payment);
    const tone = signedTone(colors, amount);
    const { kind, detail } = describe(payment, items);
    out.push(
      `  ${quoted(payment.id)} [label=<${card(
        colors,
        [
          tr([
            td(
              sectionLabel(
                colors,
                `${kind} · ${stageName(payment.stage)}${payment.estimated ? " (estimated)" : ""}`,
              ),
              { colspan: 2, pad: 7 },
            ),
          ]),
          tr([
            td(
              font(`${text(payerOf(payment))} to ${text(who(payment.payee))}`, {
                size: 10,
              }),
            ),
            td(
              amount === 0
                ? font(money(payment.amount), { size: 11, bold: true })
                : font(signed(amount), {
                    size: 11,
                    color: tone.ink,
                    bold: true,
                  }),
              { align: "RIGHT" },
            ),
          ]),
          ...(detail ? [tr([td(quiet(text(detail)), { colspan: 2 })])] : []),
          ...(payment.vat
            ? [
                tr([
                  td(quiet(`with ${money(payment.vat)} VAT`), { colspan: 2 }),
                ]),
              ]
            : []),
        ],
        amount === 0 ? {} : { outline: tone.ink, bg: tone.wash },
      )}>];`,
    );
  }

  // ---- what causes what
  // The rows of the costs table an arrow would start from: past a few of them
  // the arrows say less than their number, and one from the table says it.
  const MAX_ROW_ARROWS = 3;
  const causes = (payment: PaymentItem, to: string) => {
    const rows = new Set<string>();
    const nodes = new Set<string>();
    for (const id of payment.causedBy.origins) {
      if (rowOf.has(id)) rows.add(`costs:e${rowOf.get(id)}:e`);
      else if (origins.some((o) => o.id === id)) nodes.add(quoted(id));
    }
    for (const id of payment.causedBy.payments ?? []) {
      const from = placeOf.get(id);
      if (from === undefined) continue;
      if (from.startsWith("costs:")) rows.add(from);
      else nodes.add(from);
    }
    for (const from of nodes)
      if (from !== to) out.push(`  ${from} -> ${to} [style=dashed];`);
    if (rows.size > MAX_ROW_ARROWS)
      out.push(`  costs -> ${to} [style=dashed, label=<${rows.size} costs>];`);
    else
      for (const from of rows) out.push(`  ${from} -> ${to} [style=dashed];`);
  };
  for (const payment of others) causes(payment, quoted(payment.id));
  // a row of the costs table is caused by its work; what else causes its bill
  // (the enforcement plan behind an enforcement cost) points at the row
  lines.forEach(({ bills }, row) => {
    const facts = new Set(
      bills.flatMap((p) => p.causedBy.origins).filter((id) => !rowOf.has(id)),
    );
    for (const id of facts)
      if (origins.some((o) => o.id === id))
        out.push(`  ${quoted(id)} -> costs:w${row}:w [style=dashed];`);
  });
  if (adverse.length > 0) {
    const facts = new Set(adverse.flatMap((p) => p.causedBy.origins));
    for (const id of facts)
      if (origins.some((o) => o.id === id))
        out.push(`  ${quoted(id)} -> adverse [style=dashed];`);
  }

  // ---- recoveries the court awarded that no payment of the party carries
  items.context.droppedRecoveries.forEach((dropped, index) =>
    out.push(
      `  dropped${index} [label=<${card(
        colors,
        [
          tr([
            td(sectionLabel(colors, "Recovery awarded, but dropped"), {
              pad: 7,
            }),
          ]),
          tr([
            td(
              font(
                `${text(costTypeName(dropped.costType))}: ${money(dropped.amount)}`,
                {
                  size: 10.5,
                  bold: true,
                },
              ),
            ),
          ]),
          tr([td(quiet("no payment of the party to return it on"))]),
        ],
        { outline: colors.warning.ink, bg: colors.warning.wash },
      )}>];`,
    ),
  );

  // ---- the net result: the sum of the party's payments
  const net = sum([...items.payments]);
  const parts: [string, number][] = [
    ["Costs paid", sum(billed)],
    ["Costs recovered", sum(recovered)],
    ["Costs of the other side", sum(adverse)],
    ...others.map((payment): [string, number] => {
      const { kind, detail } = describe(payment, items);
      return [detail ? `${kind}: ${detail}` : kind, forParty(payment)];
    }),
  ];
  const provider = (entity: "lawFirm" | "barrister", label: string) => {
    const touched = items.payments.filter(
      (p) => p.payer === entity || p.payee === entity,
    );
    return touched.length === 0
      ? []
      : [
          tr([
            td(quiet(label)),
            td(
              quiet(money(sum(touched, (p) => signedAmountForItem(p, entity)))),
              {
                align: "RIGHT",
              },
            ),
          ]),
        ];
  };
  const netTone = signedTone(colors, net);
  out.push(
    `  net [label=<${card(
      colors,
      [
        tr([
          td(sectionLabel(colors, `Net result of ${partyName}`), {
            colspan: 2,
            pad: 7,
          }),
        ]),
        ...parts
          .filter(([, value]) => value !== 0)
          .map(([name, value]) =>
            tr([
              td(quiet(text(name), 9.5)),
              td(figure(value), { align: "RIGHT" }),
            ]),
          ),
        tr([
          td(font("Net result", { size: 10.5, bold: true }), {
            rule: colors.rule,
          }),
          td(font(money(net), { size: 14, color: netTone.ink, bold: true }), {
            align: "RIGHT",
            rule: colors.rule,
          }),
        ]),
        ...provider("lawFirm", "Result of the law firm"),
        ...provider("barrister", "Result of the barrister"),
      ],
      { outline: netTone.ink, bg: netTone.wash },
    )}>];`,
  );
  const into = (from: string, value: number) => {
    if (value !== 0)
      out.push(
        `  ${from} -> net [color="${signedTone(colors, value).ink}", label=<${font(signed(value), { color: signedTone(colors, value).ink, bold: true })}>];`,
      );
  };
  if (work.length > 0) into("costs", sum(billed) + sum(recovered));
  into("adverse", sum(adverse));
  for (const payment of others) into(quoted(payment.id), forParty(payment));

  out.push("}");
  return out.join("\n");
}

/** Every party's view of one outcome — see the `ledgersOfOutcome` cloud fn. */
export type OutcomeLedgers = {
  partyNames: { [partyId: string]: string };
  ledgers: { [partyId: string]: LedgerItems };
};

// the kinds of flow an arrow of the money-flow graph stands for
type FlowKind =
  | "billing"
  | "absorption"
  | "fee"
  | "judgment"
  | "adverseCost"
  | "settlement"
  | "adjustment"
  | "vat";

const FLOW_NAMES: { [kind in FlowKind]: string } = {
  billing: "Bills",
  absorption: "Own cost of the work",
  fee: "Fees",
  judgment: "Judgment debt",
  adverseCost: "Costs order",
  settlement: "Settlement",
  adjustment: "Adjustment",
  vat: "VAT",
};

/**
 * Dev tool: who pays whom in one outcome, across all parties. Each party
 * stands with its law firm and its barrister in a frame of its own; an arrow
 * is every payment of one kind from one actor to another, added up, and
 * lists them when pointed at.
 *
 * The per-party ledgers overlap: the same transfer appears in both parties'
 * ledgers, with possibly different amounts (each side computes its own
 * conservative view, e.g. adverse costs are the payer's worst case while the
 * receiver clamps by payer shares). So:
 *
 *   - every actor's net comes from its own party's ledger only;
 *   - a transfer between parties is drawn once, from the ledger of the side
 *     that pays. The receiving side's copy of it names "external" as the
 *     payer (the judgment debt of several payers, the recovered costs) or
 *     the opponent, and is left out.
 *
 * A net and the arrows at it may therefore not reconcile exactly where the
 * two views disagree: that is the model, not the drawing.
 */
export function outcomeFlowDot(
  outcome: OutcomeLedgers,
  look: LedgerLook,
): string {
  const { colors, money, stageName, costTypeName } = look;
  const name = (partyId: string): string =>
    outcome.partyNames[partyId] ?? partyId;

  const nets = new Map<string, Big>();
  const addNet = (node: string, by: Big) =>
    nets.set(node, (nets.get(node) ?? new Big(0)).add(by));
  const flows = new Map<
    string,
    { from: string; to: string; kind: FlowKind; sum: Big; details: string[] }
  >();
  const partyIds = Object.keys(outcome.ledgers);
  const nodeIds = new Map(partyIds.map((id, index) => [id, `s${index}`]));

  for (const [partyId, items] of Object.entries(outcome.ledgers)) {
    const side = nodeIds.get(partyId);
    const nodeOf = (ref: EntityRef): string => {
      const opponentId = opponentIdOf(ref);
      if (opponentId !== undefined)
        return `${nodeIds.get(opponentId) ?? "unknown"}_party`;
      return ref === "external" ? "external" : `${side}_${ref}`;
    };
    const cites = (payment: PaymentItem, kind: OriginItem["kind"]) =>
      payment.causedBy.origins.some((id) =>
        items.origins.some(
          (origin) => origin.id === id && origin.kind === kind,
        ),
      );

    for (const payment of items.payments) {
      if (entityOf(payment.payer) !== undefined)
        addNet(
          nodeOf(payment.payer),
          new Big(payment.amount).add(payment.vat ?? 0).neg(),
        );
      if (payment.payee === "external" || entityOf(payment.payee) !== undefined)
        addNet(nodeOf(payment.payee), new Big(payment.amount));
      // the payer pays the VAT on top, to the tax authority outside the case
      if (payment.vat) addNet("external", new Big(payment.vat));

      const rule = payment.causedBy.derivation?.rule;
      const copyOfTheReceivingSide =
        payment.payee === "party" &&
        (opponentIdOf(payment.payer) !== undefined ||
          (payment.payer === "external" &&
            payment.mechanism === "adjustment" &&
            (cites(payment, "quantumAward") || rule === "recovery")));
      if (copyOfTheReceivingSide) continue;

      const kind: FlowKind =
        payment.mechanism !== "adjustment"
          ? payment.mechanism
          : rule === "adverseCost"
            ? "adverseCost"
            : cites(payment, "quantumAward")
              ? "judgment"
              : cites(payment, "settlement")
                ? "settlement"
                : "adjustment";
      // one line per payment for the list an arrow shows when pointed at
      const { kind: what, detail } = describe(payment, items);
      const work = payment.causedBy.origins
        .map((id) => items.origins.find((o) => o.id === id))
        .find(isOriginOfKind("work"));
      const line =
        rule === "adverseCost" &&
        payment.causedBy.derivation?.rule === "adverseCost"
          ? `${costTypeName(payment.causedBy.derivation.costType)}, ${stageName(payment.causedBy.derivation.costStage)}`
          : work !== undefined &&
              (payment.mechanism === "billing" ||
                payment.mechanism === "absorption")
            ? `${costTypeName(work.costType)}, ${stageName(work.stage)}${work.estimated ? " (estimated)" : ""}`
            : detail
              ? `${what}: ${detail}`
              : what;
      const addFlow = (
        from: string,
        to: string,
        kind: FlowKind,
        amount: number,
      ) => {
        const key = `${from}|${to}|${kind}`;
        const flow = flows.get(key) ?? {
          from,
          to,
          kind,
          sum: new Big(0),
          details: [],
        };
        flow.sum = flow.sum.add(amount);
        flow.details.push(`${line} ${money(amount)}`);
        flows.set(key, flow);
      };
      addFlow(
        nodeOf(payment.payer),
        nodeOf(payment.payee),
        kind,
        payment.amount,
      );
      if (payment.vat)
        addFlow(nodeOf(payment.payer), "external", "vat", payment.vat);
    }
  }

  const touched = new Set(
    [...flows.values()].flatMap(({ from, to }) => [from, to]),
  );
  // the outside is where money leaves the case: it has no result of its own
  const actor = (id: string, title: string, role: string) => {
    const net = (nets.get(id) ?? new Big(0)).toNumber();
    const isOutside = id === "external";
    const tone = isOutside ? colors.neutral : signedTone(colors, net);
    return `${id} [label=<${card(
      colors,
      [
        tr([
          td(
            `${font(text(title), { size: 10.5, bold: true })}<BR ALIGN="LEFT"/>${font(text(role), { size: 8.5, color: colors.quiet })}`,
          ),
        ]),
        tr([
          td(
            badge(
              isOutside
                ? `receives ${money(net)}`
                : `net ${net > 0 ? "+" : ""}${money(net)}`,
              tone,
            ),
          ),
        ]),
      ],
      isOutside ? { bg: colors.soft } : net === 0 ? {} : { outline: tone.ink },
    )}>];`;
  };

  const out = graphStart(colors, ", compound=true");
  for (const partyId of partyIds) {
    const side = nodeIds.get(partyId);
    out.push(
      `  subgraph cluster_${side} {`,
      `    label=<${font(text(`${name(partyId)} and its advisers`.toUpperCase()), { size: 7.5, color: colors.quiet, bold: true })}>; labeljust="l"; style="rounded,filled"; fillcolor="${colors.soft}"; color="${colors.rule}"; margin=14;`,
      `    ${actor(`${side}_party`, name(partyId), "party")}`,
      ...(touched.has(`${side}_lawFirm`)
        ? [`    ${actor(`${side}_lawFirm`, "Law firm", `of ${name(partyId)}`)}`]
        : []),
      ...(touched.has(`${side}_barrister`)
        ? [
            `    ${actor(`${side}_barrister`, "Barrister", `of ${name(partyId)}`)}`,
          ]
        : []),
      `  }`,
    );
  }
  if (touched.has("external"))
    out.push(
      `  ${actor("external", "Outside the case", "courts, experts, the tax authority, the providers' own cost of their work")}`,
    );

  const flowColor: { [kind in FlowKind]: string } = {
    billing: colors.primary,
    absorption: colors.quiet,
    fee: colors.warning.ink,
    judgment: colors.ink,
    adverseCost: colors.adverse.ink,
    settlement: colors.primary,
    adjustment: colors.ink,
    vat: colors.quiet,
  };
  const MAX_LISTED = 40;
  for (const { from, to, kind, sum, details } of flows.values()) {
    const listed = details.slice(0, MAX_LISTED);
    if (details.length > listed.length)
      listed.push(`… and ${details.length - listed.length} more`);
    const tooltip = quoted(listed.join("\n")).replace(/\n/g, "\\n");
    out.push(
      `  ${from} -> ${to} [color="${flowColor[kind]}"${kind === "absorption" ? ", style=dashed" : ""}, label=<${font(FLOW_NAMES[kind], { size: 8.5 })}<BR/>${font(money(sum.toNumber()), { size: 10, color: colors.ink, bold: true })}>, tooltip=${tooltip}, labeltooltip=${tooltip}];`,
    );
  }
  out.push("}");
  return out.join("\n");
}
